import { useState, useCallback } from 'react';
import { FilterOption } from './FilterDropdown';

export interface FilterState {
  destination?: string;
  type?: string;
}

export interface UseFilterStateReturn {
  filterState: FilterState;
  setDestination: (destination: string) => void;
  setType: (type: string) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

// Pre-defined filter options based on Figma design
export const DESTINATION_OPTIONS: FilterOption[] = [
  { id: 'atacama', label: 'ATACAMA, CHILE', value: 'atacama' },
  { id: 'patagonia', label: 'PATAGONIA, CHILE', value: 'patagonia' },
  { id: 'iguazu', label: 'IGUAZU, ARGENTINA', value: 'iguazu' },
  { id: 'mendoza', label: 'MENDOZA, ARGENTINA', value: 'mendoza' },
  { id: 'santa-catarina', label: 'SANTA CATARINA, BRAZIL', value: 'santa-catarina' }
];

// Type options matching exact Figma design specifications
export const TYPE_OPTIONS: FilterOption[] = [
  { id: 'art-music', label: 'ART & MUSIC', value: 'art-music' },
  { id: 'nature', label: 'NATURE', value: 'nature' },
  { id: 'sustainability', label: 'SUSTAINABILITY', value: 'sustainability' },
  { id: 'awasi-foundation', label: 'AWASI FOUNDATION', value: 'awasi-foundation' },
  { id: 'adventure', label: 'ADVENTURE', value: 'adventure' },
  { id: 'stories', label: 'STORIES', value: 'stories' }
];

/**
 * Custom hook for managing filter state
 * Provides centralized filter state management for consistent behavior across pages
 */
export const useFilterState = (initialState: FilterState = {}): UseFilterStateReturn => {
  const [filterState, setFilterState] = useState<FilterState>(initialState);

  const setDestination = useCallback((destination: string) => {
    setFilterState(prev => ({ ...prev, destination }));
  }, []);

  const setType = useCallback((type: string) => {
    setFilterState(prev => ({ ...prev, type }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilterState({});
  }, []);

  const hasActiveFilters = Boolean(filterState.destination || filterState.type);

  return {
    filterState,
    setDestination,
    setType,
    clearFilters,
    hasActiveFilters
  };
};